import { computePosition, flip, shift, offset } from '@floating-ui/dom'
import type { SuggestionProps } from '@tiptap/suggestion'

/**
 * Shared positioning utilities for Tiptap Suggestion extensions
 * Used by SlashCommand and ColonCommand extensions
 */

export type RectStorage = {
  width: number
  height: number
  left: number
  top: number
  right: number
  bottom: number
}

/**
 * Creates a function that gets the reference client rect for positioning
 * Falls back to editor storage if clientRect is not available
 */
export function createGetReferenceClientRect(
  props: SuggestionProps,
  extensionName: string
): () => RectStorage {
  return () => {
    if (!props.clientRect) {
      return (props.editor.storage as any)[extensionName].rect
    }

    const rect = props.clientRect()

    if (!rect) {
      return (props.editor.storage as any)[extensionName].rect
    }

    return rect
  }
}

/**
 * Updates the position of a popup element using Floating UI
 */
export function updatePopupPosition(
  popupElement: HTMLDivElement | null,
  getReferenceClientRect: () => RectStorage
): void {
  if (!popupElement) return

  computePosition(
    {
      getBoundingClientRect: getReferenceClientRect,
    } as any,
    popupElement,
    {
      placement: 'bottom-start',
      middleware: [offset(8), flip(), shift({ padding: 8 })],
    }
  ).then(({ x, y }) => {
    if (popupElement) {
      popupElement.style.left = `${x}px`
      popupElement.style.top = `${y}px`
    }
  })
}

/**
 * Creates a popup element for a suggestion extension
 */
export function createPopupElement(className: string): HTMLDivElement {
  const element = document.createElement('div')
  element.className = className
  element.style.position = 'absolute'
  element.style.zIndex = '9999'
  element.style.display = 'none'
  document.body.appendChild(element)
  return element
}

/**
 * Default rect storage for suggestion extensions
 */
export const DEFAULT_RECT_STORAGE: RectStorage = {
  width: 0,
  height: 0,
  left: 0,
  top: 0,
  right: 0,
  bottom: 0,
}
